
import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { supabase } from "@/integrations/supabase/client";

interface PlatformStats {
  totalAgents: number;
  totalCreators: number;
  totalDownloads: number;
  averageRating: number;
}

const StatisticsSection: React.FC = () => {
  const [stats, setStats] = useState<PlatformStats>({
    totalAgents: 0,
    totalCreators: 0,
    totalDownloads: 0,
    averageRating: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch total agents and their sum of downloads
        const { data: agentsData, error: agentsError } = await supabase
          .from('agents')
          .select('downloads, owner_id');
        
        if (agentsError) throw agentsError;

        // Get unique creators count
        const uniqueCreators = new Set(agentsData?.map(agent => agent.owner_id) || []);
        
        // Calculate total downloads
        const totalDownloads = agentsData?.reduce((sum, agent) => sum + (agent.downloads || 0), 0) || 0;

        // Fetch average rating
        const { data: ratingsData, error: ratingsError } = await supabase
          .from('agent_feedback')
          .select('rating');

        if (ratingsError) throw ratingsError;

        // Calculate statistics
        const averageRating = ratingsData?.length ? 
          ratingsData.reduce((sum, feedback) => sum + feedback.rating, 0) / ratingsData.length : 
          0;

        setStats({
          totalAgents: agentsData?.length || 0,
          totalCreators: uniqueCreators.size,
          totalDownloads,
          averageRating
        });
      } catch (error) {
        console.error("Error fetching statistics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+`;
    }
    return num.toString();
  };

  return (
    <section className="py-20 bg-white dark:bg-gray-950">
      <div className="container mx-auto px-4">
        <FadeInWhenVisible>
          <div className="text-center mb-16">
            <Badge className="mb-2">Platform Statistics</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-white">
              Growing Community of AI Enthusiasts
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Join thousands of developers and users who are already part of our thriving ecosystem
            </p>
          </div>
        </FadeInWhenVisible>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {loading ? (
            // Loading skeletons
            Array(4).fill(0).map((_, index) => (
              <div key={index} className="text-center animate-pulse">
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4 mx-auto w-24" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-32 mx-auto" />
              </div>
            ))
          ) : (
            <>
              <FadeInWhenVisible delay={0.1}>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    {formatNumber(stats.totalAgents)}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">AI Agents Created</p>
                </div>
              </FadeInWhenVisible>

              <FadeInWhenVisible delay={0.2}>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    {formatNumber(stats.totalCreators)}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">Active Creators</p>
                </div>
              </FadeInWhenVisible>

              <FadeInWhenVisible delay={0.3}>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    {formatNumber(stats.totalDownloads)}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">Total Downloads</p>
                </div>
              </FadeInWhenVisible>

              <FadeInWhenVisible delay={0.4}>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    {stats.averageRating.toFixed(1)}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">Average Rating</p>
                </div>
              </FadeInWhenVisible>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
